import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dropdown, Menu, Select, Tooltip } from 'antd';
import {
    ArrowRightOutlined,
    ArrowUpOutlined,
    CheckCircleFilled,
    CloseOutlined,
    DislikeFilled,
    DislikeOutlined,
    DownloadOutlined,
    ExclamationCircleFilled,
    PaperClipOutlined,
    ExpandAltOutlined,
    FormOutlined,
    HistoryOutlined,
    LikeFilled,
    LikeOutlined,
    LoadingOutlined,
    PlusOutlined,
    PushpinFilled,
    PushpinOutlined,
    ShrinkOutlined,
    ThunderboltFilled,
} from '@ant-design/icons';
import { Link, useHistory } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import cn from 'classnames';
import { getCachedUserRole } from 'utils/auth';
import { Roles } from 'config/general';
import { IActivity, SYSTEM_NOTE_PREFIX, useAssistant } from './useAssistant';
import { IAssistantEntity, onFormSaved, onOpenAssistant, publishAssistantEnabled } from './launcher';
import { IActionField, IFeedback, IPendingAction, ITranscriptMessage, TTranscriptItem } from './types';
import { fillFromAction, IAssistantLocationState } from './forms';
import { downloadFile } from './api';
import Mascot from './Mascot';
import styles from './styles.module.scss';

const SUGGESTIONS = [
    'How many users do we have?',
    'Show today’s door activity',
    'Add a new user',
    'Create a visitor pass',
    'Onboard a new building',
];

/** Page-aware suggestions keyed by a path fragment; falls back to SUGGESTIONS. */
const PAGE_SUGGESTIONS: { match: string; prompts: string[] }[] = [
    { match: '/users', prompts: ['Add a new user', 'Who was added this week?', 'Import users from a list', 'Deactivate a user'] },
    {
        match: '/event-feed',
        prompts: ['Show today’s denied entries', 'Who used the front door most recently?', 'Any unusual activity overnight?'],
    },
    { match: '/sites', prompts: ['Who can open the front door?', 'Onboard a new building', 'Unlock a door now', 'Rename a door'] },
    { match: '/visitor-pins', prompts: ['Create a visitor pass', 'List active visitor passes', 'Revoke a visitor pass'] },
    { match: '/schedules', prompts: ['Create a door unlock schedule for business hours', 'Which schedules apply to the front door?'] },
    {
        match: '/user-groups',
        prompts: [
            'Create an access group with doors and people',
            'Which doors can the Staff group open?',
            'Who is in no access group?',
            'Add someone to a group',
        ],
    },
    {
        match: '/integrations',
        prompts: ['Is my property management sync healthy?', 'Which residents failed to sync?', 'Run a manual sync now', 'Set up Yardi'],
    },
    {
        match: '/health-check',
        prompts: ['Which devices are offline?', 'Reboot the offline intercom', 'Any doors with repeated denials today?'],
    },
    { match: '/hardware', prompts: ['List devices and their status', 'Which readers have not reported recently?'] },
    {
        match: '/intercom-settings',
        prompts: ['Add someone to the intercom directory', 'Who answers front-desk calls?', 'Which units have no directory entry?'],
    },
    {
        match: '/alerts',
        prompts: [
            'Which alerts are still active?',
            'What caused the last door-forced-open alert?',
            'Resolve all alerts for the Front Door',
        ],
    },
    { match: '/reports', prompts: ['Export this month\u2019s door activity as a CSV', 'Who entered after hours this week?'] },
    { match: '/announcements', prompts: ['Post an announcement about tomorrow\u2019s maintenance'] },
    { match: '/settings', prompts: ['What are our login settings?', 'Turn off email-code login'] },
    { match: '/audit-log', prompts: ['What changed in the last 24 hours?'] },
];

const DOCK_KEY = 'swiftlane.assistant.docked';
// a reload (or a form handoff followed by one) used to lose the panel and the conversation
// (production feedback 2026-11-02); both are remembered now
const OPEN_KEY = 'swiftlane.assistant.open';
const CONVERSATION_KEY = 'swiftlane.assistant.conversation';
const fieldLabel = (key: string) =>
    key
        .replace(/_ids?$/, '')
        .replace(/_/g, ' ')
        .replace(/^\w/, c => c.toUpperCase());
const fieldText = (value: unknown): string => (Array.isArray(value) ? value.map(v => String(v)).join(', ') : String(value));
const FORM_LABELS: Record<string, string> = {
    user: 'Add a user',
    visitor_pass: 'Create a visitor pass',
    door_unlock_schedule: 'Door unlock schedule',
    access_schedule: 'Access schedule',
    site: 'Add a site',
    tenant: 'Intercom directory entry',
};

type ToolResult = Extract<TTranscriptItem, { type: 'tool_result' }>;

const humanizeName = (name: string): string => {
    const verbs: Record<string, string> = { get: 'Looking up', post: 'Creating', put: 'Updating', patch: 'Updating', delete: 'Deleting' };
    const [verb, ...rest] = name.split('_');
    const words = rest.filter(w => w !== 'id').join(' ');
    return verbs[verb] ? `${verbs[verb]} ${words}` : name.replace(/_/g, ' ');
};

const StatusLine: FC<{ item: Extract<TTranscriptItem, { type: 'tool_call' }>; result?: ToolResult }> = ({ item, result }) => {
    const label = item.label || humanizeName(item.name);
    const done = !!result;
    const failed = result?.is_error;
    let icon = <LoadingOutlined spin />;
    if (done) icon = failed ? <ExclamationCircleFilled /> : <CheckCircleFilled className={styles.statusOk} />;
    const outcome = result?.label || (failed ? 'Failed' : 'Done');
    return (
        <div className={cn(styles.status, { [styles.statusError]: failed })}>
            {icon}
            <span>{done ? `${label} · ${outcome}` : label}</span>
        </div>
    );
};

/** Live activity line: what the assistant is doing right now, with elapsed time. */
const ActivityLine: FC<{ activity: IActivity }> = ({ activity }) => {
    const [, tick] = useState(0);
    useEffect(() => {
        const timer = window.setInterval(() => tick(n => n + 1), 1000);
        return () => window.clearInterval(timer);
    }, []);
    const seconds = activity.startedAt ? Math.max(0, Math.round((Date.now() - activity.startedAt) / 1000)) : 0;
    let label = activity.label || 'Thinking';
    if (activity.phase === 'tool') label = activity.label;
    else if (activity.phase === 'answering') label = 'Writing answer';
    return (
        <div className={styles.activity}>
            <LoadingOutlined spin />
            <span className={styles.activityLabel}>{label}…</span>
            <span className={styles.activityTime}>{seconds}s</span>
        </div>
    );
};

const formatSize = (size?: number): string => {
    if (!size) return '';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

/** A download the assistant produced (create_file). Served with the bearer token, so not a plain link. */
const FileChip: FC<{ name: string; size?: number; url: string }> = ({ name, size, url }) => {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const onClick = async () => {
        setBusy(true);
        setError(null);
        try {
            await downloadFile(url, name);
        } catch (e) {
            setError((e as Error).message);
        } finally {
            setBusy(false);
        }
    };
    return (
        <div>
            <button type='button' className={styles.fileChip} onClick={onClick} disabled={busy}>
                {busy ? <LoadingOutlined spin /> : <DownloadOutlined />}
                <span className={styles.fileName}>{name}</span>
                {size ? <span className={styles.fileSize}>{formatSize(size)}</span> : null}
            </button>
            {error && <div className={styles.errorBox}>{error}</div>}
        </div>
    );
};

/**
 * Assistant turns show only the final answer by default. Working notes (text the
 * model wrote before calling a tool) and the tool status lines sit behind a
 * "Show steps" toggle; while streaming, the live note shows as a muted line.
 */
/**
 * Thumbs under an answer. Up saves at once; down saves at once and then asks
 * "what did you want?" in one line, so the only way to catch a confidently wrong
 * answer costs one click (measurement package, 2026-10-23).
 */
const FeedbackRow: FC<{ messageId: string; feedback?: IFeedback | null; onRate: (messageId: string, feedback: IFeedback) => void }> = ({
    messageId,
    feedback,
    onRate,
}) => {
    const [askWhy, setAskWhy] = useState(false);
    const [why, setWhy] = useState('');
    const [thanked, setThanked] = useState(false);
    const whyRef = useRef<HTMLInputElement>(null);
    // focus the one-line box when it appears (a11y lint forbids autoFocus)
    useEffect(() => {
        if (askWhy) whyRef.current?.focus();
    }, [askWhy]);
    const rating = feedback?.rating;
    const choose = (value: 1 | -1) => {
        setThanked(false);
        if (rating === value) return;
        onRate(messageId, { rating: value });
        setAskWhy(value === -1);
    };
    const sendWhy = () => {
        const comment = why.trim();
        if (comment) onRate(messageId, { rating: -1, comment });
        setAskWhy(false);
        setWhy('');
        setThanked(true);
    };
    return (
        <div className={styles.feedback}>
            {askWhy ? (
                <span className={styles.feedbackWhy}>
                    <input
                        className={styles.feedbackInput}
                        placeholder='What did you want instead?'
                        value={why}
                        maxLength={500}
                        ref={whyRef}
                        onChange={e => setWhy(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') sendWhy();
                            if (e.key === 'Escape') setAskWhy(false);
                        }}
                    />
                    <button type='button' className={styles.feedbackSend} onClick={sendWhy}>
                        {why.trim() ? 'Send' : 'Skip'}
                    </button>
                </span>
            ) : (
                thanked && <span className={styles.feedbackThanks}>Thanks, noted.</span>
            )}
            <Tooltip title='Good answer'>
                <button
                    type='button'
                    aria-label='Good answer'
                    aria-pressed={rating === 1}
                    className={cn(styles.feedbackBtn, { [styles.feedbackOn]: rating === 1 })}
                    onClick={() => choose(1)}
                >
                    {rating === 1 ? <LikeFilled /> : <LikeOutlined />}
                </button>
            </Tooltip>
            <Tooltip title='Not what I wanted'>
                <button
                    type='button'
                    aria-label='Not what I wanted'
                    aria-pressed={rating === -1}
                    className={cn(styles.feedbackBtn, { [styles.feedbackOn]: rating === -1 })}
                    onClick={() => choose(-1)}
                >
                    {rating === -1 ? <DislikeFilled /> : <DislikeOutlined />}
                </button>
            </Tooltip>
        </div>
    );
};

const Message: FC<{
    message: ITranscriptMessage;
    streaming: boolean;
    isLast: boolean;
    activity?: IActivity;
    durationLabel?: string;
    /** Send a button's text as the next message; only live on the last assistant turn. */
    onPick?: (text: string) => void;
    /** Thumbs on this answer; only offered once the backend has stored it (message.id). */
    onRate?: (messageId: string, feedback: IFeedback) => void;
}> = ({ message, streaming, isLast, activity, durationLabel, onPick, onRate }) => {
    const [showSteps, setShowSteps] = useState(false);
    const results = new Map<string, ToolResult>();
    message.items.forEach(item => {
        if (item.type === 'tool_result') results.set(item.tool_use_id, item);
    });
    const isUser = message.role === 'user';
    const systemNotes = message.items.filter(item => item.type === 'system');
    if (isUser && systemNotes.length > 0) {
        return (
            <div className={styles.systemLine}>
                {systemNotes.map(item => (item.type === 'system' ? <span key={item.text}>✓ {item.text}</span> : null))}
            </div>
        );
    }
    // a user turn holding only tool results (left by a choice / suggestion) has nothing to show
    if (isUser && !message.items.some(item => item.type === 'text' && item.text.trim())) return null;
    const live = !isUser && streaming && isLast;
    const finalTexts = message.items.filter(item => item.type === 'text' && !item.interim && item.text.trim());
    const steps = message.items.filter(item => item.type === 'tool_call' || (item.type === 'text' && item.interim));
    const buttons = message.items.filter(item => item.type === 'choices' || item.type === 'suggestions' || item.type === 'handoff');
    const canPick = !!onPick && isLast && !streaming;
    const lastItem = message.items[message.items.length - 1];
    const liveNote =
        live && lastItem && lastItem.type === 'text' && lastItem.interim === undefined && finalTexts.length === 0 ? lastItem.text : '';
    const hasHiddenSteps = steps.length > 0;
    return (
        <div className={cn(styles.row, { [styles.rowUser]: isUser })}>
            {!isUser && (
                <div className={styles.botMark}>
                    <ThunderboltFilled />
                </div>
            )}
            <div className={styles.stack}>
                {isUser && message.items.some(item => item.type === 'attachment') && (
                    <div className={styles.attachRow}>
                        {message.items.map((item, index) =>
                            item.type === 'attachment' ? (
                                // eslint-disable-next-line react/no-array-index-key
                                <span className={styles.attachChip} key={index}>
                                    <PaperClipOutlined /> {item.name}
                                </span>
                            ) : null,
                        )}
                    </div>
                )}
                {isUser &&
                    message.items.map((item, index) =>
                        item.type === 'text' ? (
                            // eslint-disable-next-line react/no-array-index-key
                            <div className={cn(styles.bubble, styles.bubbleUser)} key={index}>
                                {item.text}
                            </div>
                        ) : null,
                    )}
                {!isUser &&
                    message.items.map(item =>
                        item.type === 'file' ? <FileChip key={item.url} name={item.name} size={item.size} url={item.url} /> : null,
                    )}
                {!isUser && (showSteps || live) && steps.length > 0 && (
                    <div className={styles.steps}>
                        {steps.map((item, index) => {
                            if (item.type === 'tool_call') {
                                // eslint-disable-next-line react/no-array-index-key
                                return <StatusLine item={item} result={results.get(item.id)} key={index} />;
                            }
                            if (item.type === 'text' && showSteps) {
                                return (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <div className={styles.note} key={index}>
                                        {item.text}
                                    </div>
                                );
                            }
                            return null;
                        })}
                    </div>
                )}
                {!isUser && liveNote && !showSteps && <div className={styles.note}>{liveNote}</div>}
                {live && activity && activity.phase !== 'idle' && (finalTexts.length === 0 || activity.phase === 'tool') && (
                    <ActivityLine activity={activity} />
                )}
                {!isUser &&
                    finalTexts.map((item, index) =>
                        item.type === 'text' ? (
                            // eslint-disable-next-line react/no-array-index-key
                            <div className={cn(styles.bubble, styles.bubbleBot)} key={index}>
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{item.text}</ReactMarkdown>
                            </div>
                        ) : null,
                    )}
                {!isUser &&
                    buttons.map(item => {
                        if (item.type === 'choices') {
                            return (
                                <div className={styles.choiceBlock} key={item.id}>
                                    <div className={styles.choiceQuestion}>{item.question}</div>
                                    <div className={styles.choiceRow}>
                                        {item.options.map(option => (
                                            <button
                                                type='button'
                                                className={styles.choiceChip}
                                                key={option}
                                                disabled={!canPick}
                                                onClick={() => onPick?.(option)}
                                            >
                                                {option}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            );
                        }
                        if (item.type === 'suggestions') {
                            return (
                                <div className={styles.suggestRow} key={item.id}>
                                    {item.options.map(option => (
                                        <button
                                            type='button'
                                            className={styles.suggestBtn}
                                            key={option.label}
                                            disabled={!canPick}
                                            onClick={() => onPick?.(option.prompt || option.label)}
                                        >
                                            <ArrowRightOutlined />
                                            {option.label}
                                        </button>
                                    ))}
                                </div>
                            );
                        }
                        if (item.type === 'handoff') {
                            const shown = Object.entries(item.fields || {}).filter(
                                ([, v]) => v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0),
                            );
                            return (
                                <div className={styles.handoff} key={item.route}>
                                    <FormOutlined />
                                    <span>
                                        The <Link to={item.route}>{item.label}</Link> form is open with these details filled in. Check it
                                        and submit; I&apos;ll pick up from there.
                                        {shown.length > 0 && (
                                            // exactly what reached the form, so the chat never promises values the form does not have
                                            <ul className={styles.handoffFields}>
                                                {shown.map(([key, value]) => (
                                                    <li key={key}>
                                                        <span>{fieldLabel(key)}:</span> {fieldText(value)}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </span>
                                </div>
                            );
                        }
                        return null;
                    })}
                {!isUser && !live && (hasHiddenSteps || (onRate && message.id && (finalTexts.length > 0 || buttons.length > 0))) && (
                    <div className={styles.footerRow}>
                        {hasHiddenSteps && (
                            <button type='button' className={styles.stepsToggle} onClick={() => setShowSteps(v => !v)}>
                                {showSteps
                                    ? 'Hide steps'
                                    : `${steps.filter(i => i.type === 'tool_call').length} step${
                                          steps.filter(i => i.type === 'tool_call').length === 1 ? '' : 's'
                                      }${durationLabel ? ` · ${durationLabel}` : ''} · Show`}
                            </button>
                        )}
                        {onRate && message.id && (finalTexts.length > 0 || buttons.length > 0) && (
                            <FeedbackRow messageId={message.id} feedback={message.feedback} onRate={onRate} />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const fieldDisplay = (field: IActionField): string => {
    if (field.display) return field.display;
    if (Array.isArray(field.value)) return field.value.map(String).join(', ');
    if (typeof field.value === 'object' && field.value !== null) return JSON.stringify(field.value);
    return String(field.value);
};

const groupActions = (actions: IPendingAction[]): { label: string; actions: IPendingAction[] }[] => {
    const groups = new Map<string, IPendingAction[]>();
    actions.forEach(action => {
        const key = action.label || action.name;
        groups.set(key, [...(groups.get(key) || []), action]);
    });
    return Array.from(groups.entries()).map(([label, items]) => ({ label, actions: items }));
};

const PREVIEW_ITEMS = 8;

const ApprovalCard: FC<{ actions: IPendingAction[]; onDecide: (approve: boolean) => void; busy: boolean }> = ({
    actions,
    onDecide,
    busy,
}) => {
    const [showAll, setShowAll] = useState(false);
    const groups = groupActions(actions);
    const highRisk = actions.some(action => action.risk === 'high');
    let title = actions.length === 1 ? 'Please confirm this change' : `Please confirm these ${actions.length} changes (one approval)`;
    if (highRisk)
        title =
            actions.length === 1 ? 'Please confirm: this is hard to undo' : `Please confirm ${actions.length} changes, some hard to undo`;
    return (
        <div className={cn(styles.approval, { [styles.approvalHigh]: highRisk })}>
            <div className={styles.approvalTitle}>{title}</div>
            {groups.map(group => (
                <div key={group.label} className={styles.approvalItem}>
                    <div className={styles.approvalLabel}>
                        {group.label}
                        {group.actions.length > 1 ? ` (${group.actions.length} items)` : ''}
                    </div>
                    {(showAll ? group.actions : group.actions.slice(0, PREVIEW_ITEMS)).map((action, index) => (
                        <div key={action.tool_use_id} className={cn({ [styles.batchItem]: group.actions.length > 1 })}>
                            {group.actions.length > 1 && <span className={styles.batchIndex}>{index + 1}</span>}
                            {(action.fields || []).map(field => (
                                <div className={styles.fieldRow} key={field.name}>
                                    <span className={styles.fieldKey}>{field.label || field.name.replace(/_/g, ' ')}</span>
                                    <span className={styles.fieldVal}>{fieldDisplay(field)}</span>
                                </div>
                            ))}
                        </div>
                    ))}
                    {group.actions.length > PREVIEW_ITEMS && (
                        <button type='button' className={styles.stepsToggle} onClick={() => setShowAll(v => !v)}>
                            {showAll ? 'Show fewer' : `Show all ${group.actions.length}`}
                        </button>
                    )}
                </div>
            ))}
            <details className={styles.details}>
                <summary>Technical details</summary>
                {actions.map(action => (
                    <pre key={action.tool_use_id}>
                        {action.summary}
                        {'\n'}
                        {JSON.stringify(action.input, null, 2)}
                    </pre>
                ))}
            </details>
            <div className={styles.approvalActions}>
                <Button type='primary' loading={busy} onClick={() => onDecide(true)}>
                    Approve
                </Button>
                <Button disabled={busy} onClick={() => onDecide(false)}>
                    Not now
                </Button>
            </div>
        </div>
    );
};

const Assistant: FC = () => {
    const [open, setOpen] = useState(() => localStorage.getItem(OPEN_KEY) === '1');
    const [expanded, setExpanded] = useState(false);
    const [docked, setDocked] = useState(() => localStorage.getItem(DOCK_KEY) === '1');
    const [draft, setDraft] = useState('');
    const [entity, setEntity] = useState<IAssistantEntity | undefined>(undefined);
    const assistant = useAssistant(open);
    const history = useHistory<IAssistantLocationState>();
    const handoffRef = useRef<string | null>(null);
    const transcriptRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const isAdmin = getCachedUserRole() === Roles.admin;
    const { pathname } = window.location;

    const messages = assistant.conversation?.messages;
    const { pending, streaming, history: pastConversations, newConversation, openConversation, send, handoff, attach } = assistant;
    const fileInputRef = useRef<HTMLInputElement>(null);
    const enabled = isAdmin && !!assistant.status?.enabled;

    // tell entry points (sidebar, page buttons) whether to render
    useEffect(() => {
        publishAssistantEnabled(enabled);
    }, [enabled]);

    // remember the panel state and the conversation across reloads
    useEffect(() => {
        localStorage.setItem(OPEN_KEY, open ? '1' : '0');
    }, [open]);
    const conversationId = assistant.conversation?.id;
    useEffect(() => {
        if (conversationId) localStorage.setItem(CONVERSATION_KEY, conversationId);
    }, [conversationId]);
    const restoredRef = useRef(false);
    useEffect(() => {
        if (!enabled || restoredRef.current) return;
        restoredRef.current = true;
        const saved = localStorage.getItem(CONVERSATION_KEY);
        if (saved && !assistant.conversation) {
            openConversation(saved).catch(() => localStorage.removeItem(CONVERSATION_KEY));
        }
    }, [enabled, assistant.conversation, openConversation]);

    // open requests from anywhere in the dashboard
    useEffect(
        () =>
            onOpenAssistant(options => {
                if (!enabled) return;
                setOpen(true);
                setEntity(options.entity);
                if (options.prompt && options.send) {
                    send(options.prompt, options.entity);
                } else if (options.prompt) {
                    setDraft(options.prompt);
                }
                window.setTimeout(() => inputRef.current?.focus(), 50);
            }),
        [enabled, send],
    );

    // keyboard: Cmd/Ctrl+K opens and focuses, Esc closes when the composer is focused
    useEffect(() => {
        const onKey = (event: KeyboardEvent) => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
                if (!enabled) return;
                event.preventDefault();
                setOpen(true);
                window.setTimeout(() => inputRef.current?.focus(), 50);
            } else if (event.key === 'Escape' && open && document.activeElement === inputRef.current) {
                setOpen(false);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [enabled, open]);

    // dock mode pushes the page content aside instead of floating over it
    useEffect(() => {
        const active = open && docked;
        document.body.classList.toggle('assistant-docked', active);
        return () => document.body.classList.remove('assistant-docked');
    }, [open, docked]);

    const toggleDock = () => {
        setDocked(current => {
            localStorage.setItem(DOCK_KEY, current ? '0' : '1');
            return !current;
        });
    };

    useEffect(() => {
        const node = transcriptRef.current;
        if (node) node.scrollTop = node.scrollHeight;
    }, [messages, pending, streaming]);

    useEffect(() => {
        if (open) inputRef.current?.focus();
    }, [open, streaming]);

    const historyMenu = useMemo(
        () => (
            <Menu
                onClick={({ key }) => {
                    if (key === 'new') newConversation();
                    else openConversation(String(key));
                }}
                items={[
                    { key: 'new', label: 'New conversation', icon: <PlusOutlined /> },
                    ...(pastConversations.length ? [{ type: 'divider' as const }] : []),
                    ...pastConversations.map(c => ({ key: c.id, label: c.title })),
                ]}
            />
        ),
        [pastConversations, newConversation, openConversation],
    );

    // The admin submitted a form the assistant opened: let the conversation continue on its own
    // (feedback 2026-10-20: "I have to manually say done").
    useEffect(
        () =>
            onFormSaved(saved => {
                if (!handoffRef.current || !assistant.conversation || streaming) return;
                handoffRef.current = null;
                const what = saved.summary ? `: ${saved.summary}` : '';
                send(`${SYSTEM_NOTE_PREFIX}Saved the ${FORM_LABELS[saved.form] || saved.form} form${what}.`);
            }),
        [assistant.conversation, streaming, send],
    );

    // A single pending create backed by a dashboard form opens that form prefilled
    // instead of an approval card; the model is told not to retry.
    useEffect(() => {
        if (!pending || pending.length !== 1 || !pending[0].form || streaming) return;
        const action = pending[0];
        if (!action.form || handoffRef.current === action.tool_use_id) return;
        handoffRef.current = action.tool_use_id;
        const fill = fillFromAction(action.form);
        handoff(action);
        history.push(fill.route, { assistantFill: fill });
    }, [pending, streaming, handoff, history]);

    const { startedAt } = assistant.activity;
    const lastDuration = useMemo(() => {
        if (streaming || !startedAt) return undefined;
        const seconds = Math.round((Date.now() - startedAt) / 1000);
        return seconds > 0 && seconds < 600 ? `${seconds}s` : undefined;
    }, [streaming, startedAt]);

    // Invisible unless the backend says the assistant is enabled for this workspace.
    if (!enabled) return null;

    const submit = () => {
        if ((!draft.trim() && assistant.attachments.length === 0) || streaming || pending) return;
        const text = draft.trim() || 'Here is a file.';
        setDraft('');
        send(text, entity);
        setEntity(undefined);
    };
    let composerPlaceholder = 'Message Swiftlane Assistant';
    if (pending) composerPlaceholder = 'Approve or decline the change above first';
    else if (assistant.attachments.length) composerPlaceholder = 'What should I do with the file?';
    const onFilesPicked = (list: FileList | null) => {
        if (!list) return;
        Array.from(list).forEach(file => attach(file));
        if (fileInputRef.current) fileInputRef.current.value = '';
    };
    const suggestions = PAGE_SUGGESTIONS.find(item => pathname.includes(item.match))?.prompts || SUGGESTIONS;

    const workspace = assistant.conversation?.workspace || assistant.status?.workspace;
    const showSwitcher = assistant.workspaces.length > 1;
    const transcript = assistant.conversation?.messages || [];

    if (!open) {
        return <Mascot onOpen={() => setOpen(true)} activity={assistant.activity} streaming={streaming} hasPending={!!pending} />;
    }

    return (
        <div
            className={cn(styles.panel, { [styles.expanded]: expanded && !docked, [styles.docked]: docked })}
            role='dialog'
            aria-label='Swiftlane Assistant'
        >
            <div className={styles.header}>
                <div className={styles.avatar}>
                    <ThunderboltFilled />
                </div>
                <div className={styles.headerText}>
                    <strong>Swiftlane Assistant</strong>
                    <span>{workspace ? workspace.name || workspace.workspace_name : 'Loading…'}</span>
                </div>
                <Dropdown overlay={historyMenu} trigger={['click']} placement='bottomRight'>
                    <button type='button' className={styles.iconBtn} aria-label='Conversations'>
                        <HistoryOutlined />
                    </button>
                </Dropdown>
                <Tooltip title='New conversation'>
                    <button type='button' className={styles.iconBtn} aria-label='New conversation' onClick={() => newConversation()}>
                        <PlusOutlined />
                    </button>
                </Tooltip>
                <Tooltip title={docked ? 'Undock' : 'Dock to the side'}>
                    <button type='button' className={styles.iconBtn} aria-label={docked ? 'Undock' : 'Dock'} onClick={toggleDock}>
                        {docked ? <PushpinFilled /> : <PushpinOutlined />}
                    </button>
                </Tooltip>
                {!docked && (
                    <button
                        type='button'
                        className={styles.iconBtn}
                        aria-label={expanded ? 'Shrink' : 'Expand'}
                        onClick={() => setExpanded(e => !e)}
                    >
                        {expanded ? <ShrinkOutlined /> : <ExpandAltOutlined />}
                    </button>
                )}
                <button type='button' className={styles.iconBtn} aria-label='Close' onClick={() => setOpen(false)}>
                    <CloseOutlined />
                </button>
            </div>

            {showSwitcher && (
                <div className={styles.workspaceBar}>
                    <span>Acting in</span>
                    <Select
                        size='small'
                        bordered={false}
                        style={{ minWidth: 180 }}
                        value={workspace?.company_id}
                        disabled={streaming}
                        onChange={value => assistant.switchWorkspace(String(value))}
                        options={assistant.workspaces.map(w => ({
                            value: w.company_id,
                            label: `${w.name || w.workspace_name}${w.force_single_sign_on ? ' (SSO)' : ''}`,
                            disabled: !!w.force_single_sign_on,
                        }))}
                    />
                </div>
            )}

            <div className={styles.transcript} ref={transcriptRef}>
                {transcript.length === 0 ? (
                    <div className={styles.empty}>
                        <div className={styles.botMark} style={{ margin: '0 auto', width: 40, height: 40, fontSize: 18 }}>
                            <ThunderboltFilled />
                        </div>
                        <h4>How can I help?</h4>
                        <p>Ask about users, doors, visitors or activity, or let me walk you through a task.</p>
                        <div className={styles.chips}>
                            {suggestions.map(s => (
                                <button type='button' className={styles.chip} key={s} onClick={() => send(s)}>
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>
                ) : (
                    transcript.map((message, index) => (
                        <Message
                            message={message}
                            streaming={streaming}
                            isLast={index === transcript.length - 1}
                            activity={assistant.activity}
                            durationLabel={index === transcript.length - 1 ? lastDuration : undefined}
                            onPick={pending ? undefined : text => send(text)}
                            onRate={assistant.rate}
                            // eslint-disable-next-line react/no-array-index-key
                            key={index}
                        />
                    ))
                )}
                {streaming && (transcript.length === 0 || transcript[transcript.length - 1].role === 'user') && (
                    // nothing has come back yet (model thinking, file being read): show a working bubble
                    // right away instead of an empty gap (feedback 2026-10-21)
                    <div className={styles.row}>
                        <div className={styles.botMark}>
                            <ThunderboltFilled />
                        </div>
                        <div className={styles.stack}>
                            <ActivityLine activity={assistant.activity} />
                        </div>
                    </div>
                )}
                {pending && <ApprovalCard actions={pending} onDecide={assistant.confirm} busy={streaming} />}
                {assistant.error && <div className={styles.errorBox}>{assistant.error}</div>}
            </div>

            <div className={styles.composer}>
                {entity && (
                    <div className={styles.entityChip}>
                        About: <strong>{entity.name || entity.email || entity.id}</strong>
                        <button type='button' aria-label='Clear' onClick={() => setEntity(undefined)}>
                            <CloseOutlined />
                        </button>
                    </div>
                )}
                {assistant.attachments.length > 0 && (
                    <div className={styles.attachRow}>
                        {assistant.attachments.map(file => (
                            <span className={styles.attachChip} key={file.id}>
                                <PaperClipOutlined /> {file.name} <em>{file.summary}</em>
                                <button type='button' aria-label='Remove' onClick={() => assistant.removeAttachment(file.id)}>
                                    <CloseOutlined />
                                </button>
                            </span>
                        ))}
                    </div>
                )}
                <div className={styles.inputWrap}>
                    <input
                        ref={fileInputRef}
                        type='file'
                        multiple
                        accept='.csv,.tsv,.txt,.md,.json,.xlsx,.xls,.pdf,.png,.jpg,.jpeg,.gif,.webp'
                        style={{ display: 'none' }}
                        onChange={e => onFilesPicked(e.target.files)}
                    />
                    <Tooltip title='Attach a file: CSV or spreadsheet of people, a list of door names, a PDF, a photo'>
                        <button
                            type='button'
                            className={styles.attachBtn}
                            aria-label='Attach a file'
                            disabled={!!pending || assistant.uploading}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            {assistant.uploading ? <LoadingOutlined spin /> : <PaperClipOutlined />}
                        </button>
                    </Tooltip>
                    <textarea
                        ref={inputRef}
                        rows={1}
                        value={draft}
                        placeholder={composerPlaceholder}
                        disabled={!!pending}
                        onChange={e => {
                            setDraft(e.target.value);
                            e.target.style.height = 'auto';
                            e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
                        }}
                        onKeyDown={e => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                submit();
                            }
                        }}
                    />
                    {streaming ? (
                        <button type='button' className={styles.stopBtn} aria-label='Stop' onClick={assistant.stop}>
                            <CloseOutlined />
                        </button>
                    ) : (
                        <button
                            type='button'
                            className={styles.sendBtn}
                            aria-label='Send'
                            disabled={(!draft.trim() && assistant.attachments.length === 0) || !!pending}
                            onClick={submit}
                        >
                            <ArrowUpOutlined />
                        </button>
                    )}
                </div>
                <div className={styles.hint}>
                    Enter to send · Shift+Enter for a new line · {/Mac/.test(navigator.platform) ? '⌘K' : 'Ctrl+K'} opens
                </div>
            </div>
        </div>
    );
};

export default Assistant;
